/**
 * GoalsTree — nested expandable list of goals with rollup progress.
 *
 * Inputs:
 *   - `goals`: flat list returned by `/api/goals`. Each goal carries
 *     `parentGoalId` (lifted from upstream `metadata_jsonb.parent_goal_id`)
 *     and `childCount` (computed by the gateway) so we can render the tree
 *     without recursive fetches.
 *
 * Rendering:
 *   - Roots = goals with `parentGoalId === null`.
 *   - Expand/collapse state is managed locally per-row (no URL state — the
 *     detail page handles deep-linking).
 *   - Progress = goal.progress (already rolled up at the gateway when
 *     target/current are set, OR === 1 when status === 'done'). For
 *     parent goals with un-set targets we additionally compute the
 *     mean of all descendant progress values; this is a UI-only helper
 *     so parent rows don't look flat when only their children carry KPIs.
 *
 * This component is pure — drag-drop reorder lives in a sibling component
 * (Phase 12 candidate; out of scope here).
 */
import type { Goal } from '@chaos/types/constitution';
import { Link } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, Target } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../lib/utils';

interface Props {
  goals: Goal[];
  /** When provided, only goals under this parent are rendered. */
  rootId?: string | null;
}

interface TreeNode {
  goal: Goal;
  children: TreeNode[];
}

function buildTree(goals: Goal[], rootId: string | null = null): TreeNode[] {
  const byParent = new Map<string | null, Goal[]>();
  for (const g of goals) {
    const k = g.parentGoalId;
    const bucket = byParent.get(k) ?? [];
    bucket.push(g);
    byParent.set(k, bucket);
  }
  function build(parentId: string | null): TreeNode[] {
    return (byParent.get(parentId) ?? [])
      .slice()
      .sort((a, b) => a.statement.localeCompare(b.statement))
      .map((goal) => ({ goal, children: build(goal.id) }));
  }
  return build(rootId);
}

/** Compute a rolled-up progress for the given node — averages descendants when own progress is null. */
export function rollupProgress(node: TreeNode): number | null {
  if (node.goal.progress != null) return node.goal.progress;
  if (node.children.length === 0) return null;
  const childProgresses = node.children.map(rollupProgress).filter((v): v is number => v != null);
  if (childProgresses.length === 0) return null;
  const sum = childProgresses.reduce((a, b) => a + b, 0);
  return sum / childProgresses.length;
}

export function GoalsTree({ goals, rootId = null }: Props) {
  const tree = React.useMemo(() => buildTree(goals, rootId ?? null), [goals, rootId]);
  if (tree.length === 0) {
    return (
      <p className="rounded border border-dashed border-border bg-muted/20 p-4 text-center text-[12px] text-muted-foreground">
        No goals at this level yet.
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-0.5">
      {tree.map((node) => (
        <TreeRow key={node.goal.id} node={node} depth={0} />
      ))}
    </ul>
  );
}

interface RowProps {
  node: TreeNode;
  depth: number;
}

function TreeRow({ node, depth }: RowProps) {
  const [open, setOpen] = React.useState(depth === 0); // roots open by default
  const hasChildren = node.children.length > 0;
  const progress = rollupProgress(node);

  return (
    <li>
      <div
        className={cn(
          'group flex items-center gap-1.5 rounded px-1.5 py-1 transition-colors hover:bg-foreground/5',
        )}
        style={{ paddingLeft: `${depth * 16 + 6}px` }}
      >
        <button
          type="button"
          aria-label={hasChildren ? (open ? 'Collapse' : 'Expand') : 'No children'}
          onClick={() => hasChildren && setOpen((o) => !o)}
          className={cn(
            'flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground transition-colors',
            !hasChildren && 'opacity-30',
            hasChildren && 'hover:text-foreground',
          )}
          disabled={!hasChildren}
        >
          {hasChildren ? (
            open ? (
              <ChevronDown className="h-3 w-3" aria-hidden />
            ) : (
              <ChevronRight className="h-3 w-3" aria-hidden />
            )
          ) : (
            <Target className="h-3 w-3" aria-hidden />
          )}
        </button>
        <Link
          to="/goals/$goalId"
          params={{ goalId: node.goal.id }}
          className="flex flex-1 items-center gap-2 truncate text-[13px] text-foreground"
        >
          <span className="truncate">{node.goal.statement}</span>
          <span className="ml-auto flex shrink-0 items-center gap-2">
            <ProgressDot value={progress} />
            <span className="font-mono tabular-nums text-[11px] text-muted-foreground">
              {progress == null ? '—' : `${Math.round(progress * 100)}%`}
            </span>
            <StatusChip status={node.goal.status} />
          </span>
        </Link>
      </div>
      {open && hasChildren ? (
        <ul className="flex flex-col gap-0.5">
          {node.children.map((child) => (
            <TreeRow key={child.goal.id} node={child} depth={depth + 1} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function ProgressDot({ value }: { value: number | null }) {
  if (value == null) return <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-muted" />;
  let color = 'bg-success';
  if (value < 0.34) color = 'bg-destructive';
  else if (value < 0.67) color = 'bg-warning';
  return <span aria-hidden className={cn('h-1.5 w-1.5 rounded-full', color)} />;
}

function StatusChip({ status }: { status: Goal['status'] }) {
  const label: Record<Goal['status'], string> = {
    open: 'Open',
    in_progress: 'Active',
    blocked: 'Blocked',
    done: 'Done',
    dropped: 'Dropped',
  };
  const cls: Record<Goal['status'], string> = {
    open: 'border-border bg-muted text-muted-foreground',
    in_progress: 'border-warning/40 bg-warning/10 text-warning-foreground',
    blocked: 'border-destructive/40 bg-destructive/10 text-destructive-foreground',
    done: 'border-success/40 bg-success/10 text-success-foreground',
    dropped: 'border-border bg-muted text-muted-foreground',
  };
  return (
    <span
      className={cn(
        'rounded border px-1 py-0.5 text-[9px] font-medium uppercase tracking-wider',
        cls[status],
      )}
    >
      {label[status]}
    </span>
  );
}
